@use '@/styles/tokens' as *;

.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  margin: 0;
  padding: 0;
  list-style: none;
}
.row {
  display: flex;
  gap: var(--space-5);
  align-items: flex-start;
}
.thumb {
  width: 86px;
  height: 86px;
  padding: var(--space-2);
  border-radius: $radius-sm;
  background: $color-neutral-100;
  object-fit: cover;
  box-sizing: border-box;
}
.details {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0; // required for the name's text-overflow ellipsis to take effect in a flex child
}
.nameAndDate {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.name {
  font-size: $font-size-lg;
  font-weight: $font-weight-regular;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.date {
  font-size: $font-size-xs;
  font-weight: $font-weight-regular;
  color: var(--text-tertiary);
}

// Order-level written review (ticket 02), rendered once beneath the device rows (Figma
// `rFHoKFvO8DtXXMwDSfHKBn` node `1672:456885`/`1672:456893`).
.review {
  display: flex;
  flex-direction: column;
  // Figma measures 14px between the date line and the box; no bound variable, and it sits
  // equidistant between var(--space-3) (12px) and var(--space-4) (16px) — var(--space-3) matches
  // the box's own internal rhythm (padding and paragraph gap both measure 12px exactly) so it
  // keeps one gap value across the whole block rather than introducing a second, off-by-2px one.
  gap: var(--space-3);
  margin-top: var(--space-5);
}
.reviewDate {
  display: flex;
  align-items: center;
  // Figma measures 6px between the CalendarBlank icon and the text; no bound variable, and it
  // sits equidistant between $space-1 (4px) and $space-2 (8px) — $space-2 chosen since the
  // codebase's nearest precedent for an icon+label row (`order-summary-card.module.scss`
  // `.replaced`) pairs a much smaller 14px icon with $space-1, and this icon is half again
  // larger (20px).
  gap: var(--space-2);
  margin: 0;
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: var(--text-tertiary);
}
.reviewBox {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  // Figma measures 13px padding; no bound variable, and it's the box's own internal rhythm
  // value (see `.review`'s gap comment above) rounded to the nearest token, var(--space-3) (12px).
  padding: var(--space-3);
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  // Figma `rFHoKFvO8DtXXMwDSfHKBn` node `1672:456885` measures **10px** here, and binds no radius
  // variable (only Surface/Card, Border/Subtle and Text/Tertiary are bound). 10px falls exactly
  // between $radius-lg (8px) and $radius-xl (12px), so "nearest token" does not decide it. The
  // tiebreak is `warranty-detail-drawer.module.scss`'s `.reasonCard`
  // (`src/features/requests/warranty/components/`) — the same bordered-text-block treatment, whose
  // background, border and radius this matches verbatim rather than introducing a second value.
  // Not to be confused with the drawer *shell*'s own top corners, which genuinely do measure 20px
  // (node `1672:456851`) — see `overlay-chrome.module.scss`'s `.bodyCard`.
  border-radius: var(--radius-xl);
}
.reviewParagraph {
  margin: 0;
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: var(--text-tertiary);
  // The component splits the comment on `\n\n` into one <p> per block, which collapses a *single*
  // newline the Customer typed into a space. `pre-line` keeps those breaks (and still collapses
  // runs of spaces and indentation, unlike `pre-wrap`), so a line-broken review reads as written
  // while the blank-line split keeps its 12px inter-paragraph gap.
  white-space: pre-line;
}

// No Figma frame covers the failure state, so it reuses the sibling WarrantyClaimsDrawer's centred
// slot and tokens (ADR-0023's twin-drawer convention) — a retry per the spec's decided copy.
.notice {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  text-align: center;
}

.noticeText {
  margin: 0;
  font-size: $font-size-md;
  color: var(--text-primary);
}
